"use client";

import React from "react";
import { Check, ArrowRight, Shield, HeartPulse, LineChart, Car } from "lucide-react";
import { INSURANCE_PRODUCTS, InsuranceProduct } from "@/lib/demos/insuranceData";

interface InsuranceProductsSectionProps {
  currency: "INR" | "USD";
  onSelectProduct: (product: InsuranceProduct) => void;
}

export default function InsuranceProductsSection({
  currency,
  onSelectProduct,
}: InsuranceProductsSectionProps) {
  const sym = currency === "USD" ? "$" : "₹";

  const getProductIcon = (category: string) => {
    switch (category) {
      case "Term Life":
        return <Shield className="w-5 h-5 text-blue-400" />;
      case "Health & Medical":
        return <HeartPulse className="w-5 h-5 text-emerald-400" />;
      case "Wealth & ULIP":
        return <LineChart className="w-5 h-5 text-indigo-400" />;
      default:
        return <Car className="w-5 h-5 text-amber-400" />;
    }
  };

  return (
    <section className="py-20 bg-slate-950 border-t border-slate-900">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-950/70 border border-blue-800/60 text-blue-300 text-xs font-semibold mb-3">
            Institutional Portfolio
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight">
            Comprehensive Life, Health &amp; Wealth Solutions
          </h2>
          <p className="text-slate-400 text-sm sm:text-base mt-3">
            Every policy is backed by world-class reinsurance syndicates and automated digital underwriting, ensuring transparent coverage contracts and rapid claim disbursements.
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {INSURANCE_PRODUCTS.map((prod) => {
            const sum = currency === "USD" ? prod.defaultSumAssuredUSD : prod.defaultSumAssuredINR;
            const prem = currency === "USD" ? prod.indicativeAnnualPremiumUSD : prod.indicativeAnnualPremiumINR;

            return (
              <div
                key={prod.id}
                className="flex flex-col justify-between rounded-2xl bg-slate-900/80 border border-slate-800 hover:border-blue-700/60 p-6 transition-all duration-300 hover:shadow-xl hover:shadow-blue-950/50 relative group"
              >
                {prod.badge && (
                  <div className="absolute -top-2.5 right-6 px-2.5 py-0.5 rounded-full bg-blue-600 text-white text-[10px] font-bold tracking-wider uppercase shadow-md shadow-blue-950">
                    {prod.badge}
                  </div>
                )}

                <div>
                  {/* Category & Icon */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-10 h-10 rounded-xl bg-slate-800/80 flex items-center justify-center border border-slate-700">
                      {getProductIcon(prod.category)}
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-950/70 border border-emerald-800/50 text-emerald-300">
                      {prod.taxBenefit}
                    </span>
                  </div>

                  <h3 className="text-base font-bold text-white group-hover:text-blue-300 transition-colors">
                    {prod.name}
                  </h3>

                  <p className="text-xs text-slate-400 mt-2 line-clamp-3 leading-relaxed">
                    {prod.tagline}
                  </p>

                  {/* Pricing & Sum Assured Box */}
                  <div className="mt-5 p-3 rounded-xl bg-slate-950/90 border border-slate-800/80">
                    <div className="text-[10px] text-slate-500 font-semibold uppercase">
                      Default Sum Assured
                    </div>
                    <div className="text-base font-extrabold text-white mt-0.5">
                      {sym}{sum.toLocaleString()}
                    </div>
                    <div className="text-[11px] text-blue-400 font-medium mt-1">
                      Indicative ~{sym}{prem.toLocaleString()} / year
                    </div>
                  </div>

                  {/* Benefits List */}
                  <div className="mt-5 space-y-2">
                    <div className="text-[11px] font-semibold text-slate-300 uppercase tracking-wider">
                      Included Protection:
                    </div>
                    {prod.keyBenefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-xs text-slate-300">
                        <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
                        <span>{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Bottom CTA */}
                <div className="mt-6 pt-4 border-t border-slate-800/80">
                  <div className="text-[10px] text-slate-500 mb-3 font-medium">
                    {prod.claimRatio}
                  </div>
                  <button
                    type="button"
                    onClick={() => onSelectProduct(prod)}
                    className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-blue-600/10 hover:bg-blue-600 text-blue-400 hover:text-white border border-blue-500/30 hover:border-blue-500 text-xs font-bold transition-all cursor-pointer"
                  >
                    <span>Get Custom Proposal</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
